import React, { useState } from 'react';
import { Button, Divider, Stack } from '@mui/material';

import { RoofPlane } from '@paragon/contract-library';
import { EditedRoofPlaneProperties } from '@paragon/layout';
import { PropertiesPanel } from '@paragon/layout-models';

import { TransformationButtons } from '../../../../../library/transformations/controls/TransformationButtons';
import {
  beginCuttingRoofPlane,
  editRoofPlanes,
  finishCuttingRoofPlane,
  launchFasciaHeightsTool,
} from '../../../../../store/actions/layout/roofPlaneActions';
import { isLayoutModeViewOnly } from '../../../../../store/selectors/layoutModeSelectors';
import { propertiesShouldBeViewOnly } from '../../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { ProjectHierarchyContextSelects } from '../ProjectHierarchyContextSelects';
import { PropertiesPanelMenuButton } from '../PropertiesPanelMenuButton';
import { AutoCutPlanesPropertiesPanelButton } from './AutoCutPlanesButtons';
import { PlaneCutsEditor } from './PlaneCutsEditor';
import { PlanePropertyControls } from './PlanePropertyControls';

/*
  Note that we strongly desire to keep this panel as visually similar to the ceiling plane properties panel as
    possible, as well as the bulk properties panels for roof and ceiling planes. Please consider updating all these
    panels when making any changes here.
 */
export function RoofPlaneProperties(props: { value: RoofPlane }) {
  const dispatch = useAppDispatch();
  const viewOnlyProperties = useAppSelector(propertiesShouldBeViewOnly);
  const viewOnlyLayout = useAppSelector(isLayoutModeViewOnly);
  const viewOnly = viewOnlyProperties || viewOnlyLayout;
  const [isSaving, setIsSaving] = useState(false);

  async function onChange(editedProperties: EditedRoofPlaneProperties) {
    if (isSaving) {
      throw new Error('Overlapping save of roof plane prevented. Someone forgot to disable a control when saving.');
    }

    setIsSaving(true);

    try {
      await dispatch(editRoofPlanes({ guid: props.value.guid, editedProperties }));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <PropertiesPanel
      title="Roof Plane Properties"
      titleButton={<PropertiesPanelMenuButton type="roofPlane" guid={props.value.guid} />}
    >
      <Stack spacing={2} className="clickable-during-picking">
        <TransformationButtons />

        <Divider />

        <ProjectHierarchyContextSelects
          levelGuid={props.value.levelGuid}
          onChange={async (levelGuid) => await onChange({ levelGuid })}
          disabled={viewOnly}
        />

        <PlanePropertyControls
          type="roof"
          properties={props.value}
          onChange={async (changes) => {
            await onChange(changes);
          }}
          disabled={viewOnly}
        />

        <Divider />

        <Button
          variant="outlined"
          onClick={() => {
            dispatch(launchFasciaHeightsTool());
          }}
          disabled={viewOnly}
        >
          Adjust Fascia Height
        </Button>

        <PlaneCutsEditor
          roofPlaneCuts
          basePlaneGuid={props.value.guid}
          cuts={props.value.cuts}
          onStartPicking={async () => {
            await dispatch(beginCuttingRoofPlane(props.value.guid));
          }}
          onFinishPicking={() => {
            dispatch(finishCuttingRoofPlane());
          }}
          disabled={viewOnly}
        />

        <AutoCutPlanesPropertiesPanelButton roofPlaneGuids={[props.value.guid]} disabled={viewOnly} />
      </Stack>
    </PropertiesPanel>
  );
}
